import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api, fmt } from "../lib/api";
import { Rocket, TrendingUp, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function Crash() {
  const { user, currency, refresh } = useApp();
  const [searchParams] = useSearchParams();
  const [bet, setBet] = useState(currency === "HUF" ? 500 : 1);
  const [autoCashout, setAutoCashout] = useState(() => {
    const p = searchParams.get("auto_cashout");
    return p ? Number(p) : 2.0;
  });
  const [state, setState] = useState("idle"); // idle | running | cashed | crashed
  const [multiplier, setMultiplier] = useState(1.00);
  const [crashPoint, setCrashPoint] = useState(null);
  const [payout, setPayout] = useState(0);
  const [roundId, setRoundId] = useState(null);
  const [history, setHistory] = useState([]);
  const startTsRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef("idle");
  const autoCashoutRef = useRef(null);

  useEffect(() => {
    fetchHistory();
    const iv = setInterval(fetchHistory, 8000);
    return () => clearInterval(iv);
  }, []);

  const fetchHistory = async () => {
    try {
      const r = await api.get("/instant/crash/history", { params: { limit: 20 } });
      setHistory(r.data);
    } catch {}
  };

  const startRound = async () => {
    if (state === "running") return;
    try {
      const body = { bet: Number(bet), currency };
      if (autoCashout && autoCashout > 1.0) body.auto_cashout = Number(autoCashout);
      const r = await api.post("/instant/crash/start", body);
      setRoundId(r.data.round_id);
      setState("running");
      stateRef.current = "running";
      autoCashoutRef.current = body.auto_cashout || null;
      setMultiplier(1.00);
      setCrashPoint(null);
      setPayout(0);
      startTsRef.current = performance.now();
      animate();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start");
    }
  };

  const animate = () => {
    const tick = () => {
      const elapsed = (performance.now() - startTsRef.current) / 1000;
      // Exponential growth: multiplier(t) = 1.06^t (grows faster after 3s)
      const m = Math.max(1.00, Math.pow(1.06, elapsed));
      setMultiplier(Number(m.toFixed(2)));
      if (autoCashoutRef.current && m >= autoCashoutRef.current && stateRef.current === "running") {
        cashOut(m);
        return;
      }
      if (stateRef.current === "running") {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const cashOut = async (currentM) => {
    if (stateRef.current !== "running" || !roundId) return;
    stateRef.current = "cashing";
    cancelAnimationFrame(rafRef.current);
    try {
      const r = await api.post("/instant/crash/cashout", {
        round_id: roundId, multiplier: Number((currentM ?? multiplier).toFixed(2)),
      });
      if (r.data.state === "cashed") {
        setState("cashed");
        stateRef.current = "cashed";
        setPayout(r.data.payout);
        setCrashPoint(r.data.crash_point);
        toast.success(`Cashed out ${r.data.multiplier}x → ${fmt(r.data.payout, currency)}`);
      } else {
        setState("crashed");
        stateRef.current = "crashed";
        setCrashPoint(r.data.crash_point);
        toast.error(`Crashed at ${r.data.crash_point}x`);
      }
      refresh();
      fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      stateRef.current = "running";
      animate();
    }
  };

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const color = state === "crashed" ? "text-red-500" : state === "cashed" ? "text-emerald-400" : "text-[#d4af37]";

  return (
    <div className="max-w-[1300px] mx-auto px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <Rocket className="text-[#d4af37]" size={32}/>
        <h1 className="font-serif text-4xl">Crash</h1>
        <span className="text-xs text-white/40 uppercase tracking-widest ml-2">Provably-fair · 3% edge</span>
      </div>

      {/* Recent multipliers ticker */}
      <div className="lux-card p-3 mb-6 flex items-center gap-2 overflow-x-auto">
        <span className="text-xs uppercase tracking-widest text-white/40 shrink-0 pr-2">Recent</span>
        {history.map((h, i) => (
          <span key={i} data-testid={`crash-hist-${i}`}
                className={`px-2 py-1 rounded text-xs font-bold shrink-0 ${h.crash_point >= 2 ? "bg-emerald-900/40 text-emerald-300" : "bg-red-900/40 text-red-300"}`}>
            {h.crash_point.toFixed(2)}x
          </span>
        ))}
        {history.length === 0 && <span className="text-xs text-white/30">—</span>}
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="lux-card p-8 min-h-[480px] flex flex-col items-center justify-center relative overflow-hidden">
          {/* animated grid backdrop */}
          <div className="absolute inset-0 opacity-20"
               style={{ backgroundImage: "linear-gradient(rgba(212,175,55,.3) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,.3) 1px, transparent 1px)", backgroundSize: "40px 40px" }}/>
          <div className="relative z-10 text-center">
            <div className={`font-serif text-8xl md:text-9xl ${color} transition`}>
              {multiplier.toFixed(2)}x
            </div>
            {state === "idle" && <div className="text-white/50 mt-4 uppercase tracking-widest text-xs">Set your bet & launch</div>}
            {state === "running" && <div className="text-emerald-400 mt-4 uppercase tracking-widest text-xs animate-pulse">FLYING · Cash out anytime</div>}
            {state === "cashed" && <div className="text-emerald-400 mt-4 font-bold">✈️ Cashed at {multiplier.toFixed(2)}x · +{fmt(payout, currency)}</div>}
            {state === "crashed" && <div className="text-red-500 mt-4 font-bold">💥 Crashed at {crashPoint?.toFixed(2)}x</div>}
          </div>
        </div>

        <div className="lux-card p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Bet</label>
            <input data-testid="crash-bet" type="number" value={bet} onChange={e=>setBet(e.target.value)} disabled={state==="running"}
                   className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-3 font-serif text-xl focus:border-[#d4af37]"/>
            <div className="flex gap-2 mt-2">
              {[2, 5, 10].map(x => (
                <button key={x} className="btn-ghost px-3 py-1 rounded text-xs" disabled={state==="running"}
                        onClick={()=>setBet(b => Number(b) * x)}>×{x}</button>
              ))}
              <button className="btn-ghost px-3 py-1 rounded text-xs" disabled={state==="running"}
                      onClick={()=>setBet(b => Math.max(1, Math.floor(Number(b) / 2)))}>÷2</button>
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Auto Cashout (optional)</label>
            <input data-testid="crash-auto" type="number" step="0.1" value={autoCashout} onChange={e=>setAutoCashout(e.target.value)} disabled={state==="running"}
                   className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 focus:border-[#d4af37]"/>
          </div>
          <div className="rounded bg-black/40 border border-white/10 p-3 text-xs text-white/50">
            Balance: <span className="text-[#d4af37] font-bold">{fmt(currency==="HUF"?user?.balance_huf:user?.balance_eur, currency)}</span>
          </div>
          {state === "running" ? (
            <button data-testid="crash-cashout" onClick={()=>cashOut()}
                    className="w-full btn-gold px-6 py-4 rounded-md text-lg animate-pulse">
              CASH OUT · {multiplier.toFixed(2)}x
            </button>
          ) : (
            <button data-testid="crash-start" onClick={startRound}
                    className="w-full btn-burgundy px-6 py-4 rounded-md text-lg uppercase tracking-widest">
              🚀 Launch
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
